export type Channel = "web" | "ait";
export * from "./commerce";

export interface IdentityPort {
	readonly channel: Channel;
	getIdentity(): Promise<{ value: string; kind: "install" | "toss" }>;
}

export interface CameraPort {
	pickReceipt(): Promise<File | null>;
}

export interface SharePort {
	share(url: string, title: string): Promise<"shared" | "copied" | "cancelled">;
}

export interface HapticPort {
	tap(): void;
}

export interface ThemePort {
	getTheme(): "light" | "dark";
	setTheme(theme: "light" | "dark"): void;
}

export interface AnalyticsPort {
	track(
		name: string,
		props: Record<string, string | number | boolean | string[] | null>,
	): void;
	flush(): Promise<void>;
	setEnabled(enabled: boolean): void;
}

const ANALYTICS_PRIVATE_KEYS = new Set([
	"receiptId",
	"recordId",
	"requestId",
	"storeDisplay",
	"merchant",
	"totalAmount",
	"amount",
	"memo",
	"itemName",
	"homeItemName",
	"notificationText",
	"rawText",
	"filename",
	"fileName",
	"identity",
	"installId",
	"channelIdentity",
]);

/** The queue is persistent, so reject private values before serialisation. */
export function isPrivacySafeAnalyticsProps(
	props: Record<string, string | number | boolean | string[] | null>,
): boolean {
	return Object.entries(props).every(([key, value]) => {
		if (ANALYTICS_PRIVATE_KEYS.has(key)) return false;
		if (typeof value !== "string") return true;
		return !/(?:\b[\da-f]{8}-[\da-f-]{27,}\b|\b(?:install|toss):)/i.test(value);
	});
}

export type AnonymousKeyResult =
	| { type: "HASH"; hash: string }
	| "ERROR"
	| "INVALID_CATEGORY"
	| undefined;

export function createAitIdentity(
	storage: Storage | undefined,
	resolveAnonymousKey: () => Promise<AnonymousKeyResult>,
): IdentityPort {
	const install = createInstallIdentity(storage, "ait");
	const bindingKey = "sobidogam:ait:identity";
	return {
		channel: "ait",
		async getIdentity() {
			const persisted = storage?.getItem(bindingKey);
			if (persisted?.startsWith("toss:") || persisted?.startsWith("install:"))
				return {
					value: persisted,
					kind: persisted.startsWith("toss:") ? "toss" : "install",
				};
			try {
				const result = await resolveAnonymousKey();
				if (
					result &&
					typeof result === "object" &&
					result.type === "HASH" &&
					result.hash.trim()
				) {
					const value = `toss:${result.hash.trim()}`;
					storage?.setItem(bindingKey, value);
					return { value, kind: "toss" };
				}
			} catch {
				// SDK error, unsupported version, and invalid category all fail open to
				// the stable install identity so local recording never becomes gated.
			}
			const fallback = await install.getIdentity();
			storage?.setItem(bindingKey, fallback.value);
			return fallback;
		},
	};
}

export async function identityHeaders(
	identity: IdentityPort,
	clientVersion: string,
): Promise<Record<string, string>> {
	const resolved = await identity.getIdentity();
	const installId = resolved.value.startsWith("install:")
		? resolved.value.slice("install:".length)
		: "toss-bound";
	return {
		"X-Install-Id": installId,
		"X-Channel": identity.channel,
		"X-Channel-Identity": resolved.value,
		"X-Client-Version": clientVersion,
	};
}

export function createInstallIdentity(
	storage: Storage | undefined,
	channel: Channel,
): IdentityPort {
	const key = `sobidogam:${channel}:install-id`;
	return {
		channel,
		async getIdentity() {
			let value = storage?.getItem(key);
			if (!value) {
				value = crypto.randomUUID();
				storage?.setItem(key, value);
			}
			return { value: `install:${value}`, kind: "install" };
		},
	};
}

export function createWebSharePort(): SharePort {
	return {
		async share(url, title) {
			const browserNavigator = navigator as Navigator & {
				share?: (data: ShareData) => Promise<void>;
				clipboard?: Clipboard;
			};
			if (typeof navigator !== "undefined" && browserNavigator.share) {
				try {
					await browserNavigator.share({ url, title });
					return "shared";
				} catch {
					return "cancelled";
				}
			}
			await browserNavigator.clipboard?.writeText(url);
			return "copied";
		},
	};
}

export function createBrowserHapticPort(enabled = true): HapticPort {
	return {
		tap: () => {
			if (enabled) navigator.vibrate?.(12);
		},
	};
}

export function createAnalyticsPort(
	config: {
		enabled?: boolean;
		apiBaseUrl?: string;
		storage?: Storage;
		identity?: IdentityPort;
		clientVersion?: string;
		fetcher?: typeof fetch;
	} = {},
): AnalyticsPort {
	const queueKey = "sobidogam:analytics-queue";
	const storage = config.storage;
	const fetcher = config.fetcher ?? globalThis.fetch;
	let enabled = config.enabled ?? true;
	let flushing = false;
	type QueuedEvent = {
		name: string;
		at: string;
		props: Record<string, string | number | boolean | string[] | null>;
	};
	const readQueue = (): QueuedEvent[] => {
		try {
			const value = JSON.parse(storage?.getItem(queueKey) ?? "[]");
			return Array.isArray(value) ? value.slice(-200) : [];
		} catch {
			return [];
		}
	};
	const writeQueue = (events: QueuedEvent[]) =>
		storage?.setItem(queueKey, JSON.stringify(events.slice(-200)));
	const port: AnalyticsPort = {
		track(name, props) {
			if (!enabled) return;
			if (!isPrivacySafeAnalyticsProps(props)) return;
			const queue = readQueue();
			queue.push({ name, at: new Date().toISOString(), props });
			writeQueue(queue);
			if (queue.length >= 20) void port.flush();
		},
		async flush() {
			if (
				!enabled ||
				flushing ||
				!config.apiBaseUrl ||
				!config.identity ||
				!fetcher
			)
				return;
			const events = readQueue().slice(0, 20);
			if (!events.length) return;
			flushing = true;
			try {
				const headers = await identityHeaders(
					config.identity,
					config.clientVersion ?? "0.3.0",
				);
				const response = await fetcher(
					`${config.apiBaseUrl.replace(/\/$/, "")}/api/v1/events`,
					{
						method: "POST",
						headers: { ...headers, "Content-Type": "application/json" },
						body: JSON.stringify({ events }),
					},
				);
				if (response.ok) writeQueue(readQueue().slice(events.length));
			} catch {
				// Offline is expected. The bounded queue is retried on the next flush.
			} finally {
				flushing = false;
			}
		},
		setEnabled(next) {
			enabled = next;
			if (!next) writeQueue([]);
		},
	};
	return port;
}
